import React, { useRef } from "react";
import { useDialogFocus } from "../../hook/useFormKeyboard";
import { BORDER_STRONG, DESTRUCTIVE, TEAL, TEXT, TEXT_MUTED } from "../../components/list";

export const INK = "#05292A";
export const HEAD_BG = "#FAFBFB";
export const ERROR_BG = "#FDF5F5";
export const FONT = "Inter, sans-serif";

export const tr = (language: any, key: string): string => language?.[key] ?? key;
export const fill = (text: string, params: Record<string, string | number>) =>
  text.replace(/\{(\w+)\}/g, (match, name) => (params[name] === undefined ? match : String(params[name])));

// Hover and focus need real CSS: an inline <style>, as the host build drops module-only classes.
export const OT_CSS = `
.ot-field { outline: none; }
.ot-field::placeholder { color: #898989; opacity: 1; }
.ot-field:focus, .ot-field:focus-visible { outline: 2px solid #0F7679 !important; outline-offset: -1px; border-color: #0F7679 !important; }
.ot-field[aria-invalid="true"]:focus, .ot-field[aria-invalid="true"]:focus-visible { outline-color: #BA0E13 !important; border-color: #BA0E13 !important; }
.ot-field:disabled { background-color: #F9FBFB; color: #898989; }
.ot-btn:focus-visible, .ot-icon-btn:focus-visible, .ot-disclosure:focus-visible, .ot-check:focus-visible, .ot-link:focus-visible {
  outline: 2px solid #0F7679 !important; outline-offset: 2px;
}
.ot-primary:not(:disabled):hover { background-color: #0D5759 !important; }
.ot-secondary:not(:disabled):hover { background-color: #F3FAFA !important; }
.ot-icon-btn { background-color: transparent; color: #6A7878; }
.ot-icon-btn:hover { background-color: #EBF0F0; color: #BA0E13; }
.ot-icon-btn.ot-close:hover { color: #222525; }
.ot-disclosure:hover span { color: #0F7679; }
.ot-link:hover { text-decoration: underline; }
.ot-row > td { background-color: #FFFFFF; }
.ot-row:hover > td { background-color: #F9FBFB; }
.ot-row:focus-within > td { background-color: #F3FAFA; }
.ot-pick-row > td { background-color: #FFFFFF; cursor: pointer; }
.ot-pick-row:hover > td { background-color: #F9FBFB; }
.ot-pick-row[data-selected="true"] > td { background-color: #F3FAFA; }
.ot-pick-row[data-disabled="true"] > td { cursor: default; background-color: #FFFFFF; }
@keyframes ot-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes ot-pop { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: none; } }
@keyframes ot-sheet { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; } }
.ot-backdrop { animation: ot-fade 180ms ease-out both; }
.ot-pop { animation: ot-pop 200ms cubic-bezier(.2,.8,.2,1) both; }
.ot-sheet { animation: ot-sheet 220ms cubic-bezier(.2,.8,.2,1) both; }
@media (prefers-reduced-motion: reduce) { .ot-backdrop, .ot-pop, .ot-sheet { animation: none; } }
`;

export const VISUALLY_HIDDEN: React.CSSProperties = {
  position: "absolute",
  width: "1px",
  height: "1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

type PillProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary";
  // 48px on phones, 36px otherwise.
  large?: boolean;
};

/** Teal primary pill or outlined secondary pill. */
export const PillButton = React.forwardRef<HTMLButtonElement, PillProps>(
  ({ variant = "secondary", large, style, className, disabled, children, ...rest }, ref) => (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      className={`ot-btn ${variant === "primary" ? "ot-primary" : "ot-secondary"} ${className ?? ""}`}
      style={{
        height: large ? "48px" : "36px",
        padding: large ? "0 20px" : "0 18px",
        borderRadius: "30px",
        border: variant === "primary" ? "none" : `1px solid ${BORDER_STRONG}`,
        backgroundColor: variant === "primary" ? TEAL : "#FFFFFF",
        color: variant === "primary" ? "#FFFFFF" : TEXT,
        fontFamily: FONT,
        fontSize: "14px",
        fontWeight: 500,
        lineHeight: "20px",
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        flex: "none",
        boxSizing: "border-box",
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  ),
);
PillButton.displayName = "PillButton";

/** Small rounded status chip: warning (needs quantities) or grey (in template); truncate cuts a long one. */
export const Chip: React.FC<{ tone: "warn" | "grey"; id?: string; truncate?: boolean; children: React.ReactNode }> = ({ tone, id, truncate, children }) => (
  <span
    id={id}
    title={truncate && typeof children === "string" ? children : undefined}
    style={{
      display: "inline-flex",
      alignItems: "center",
      flex: "none",
      padding: "1px 8px",
      borderRadius: "999px",
      backgroundColor: tone === "warn" ? "#FBF1DE" : "#F2F4F3",
      color: tone === "warn" ? "#8A5A00" : "#606060",
      fontSize: "11.5px",
      fontWeight: 500,
      lineHeight: "18px",
      whiteSpace: "nowrap",
      ...(truncate ? { display: "block", flex: "0 1 auto", minWidth: 0, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" } : {}),
    }}
  >
    {children}
  </span>
);

/** Shared backdrop: centred card on desktop, bottom sheet on phones. */
export const DialogFrame: React.FC<{
  phone: boolean;
  onClose: () => void;
  dialogRef: React.RefObject<HTMLDivElement>;
  labelledBy: string;
  describedBy?: string;
  width: number;
  role?: "dialog" | "alertdialog";
  sheet?: boolean;
  // A fixed height keeps a list dialog from jumping while its results change.
  fixedHeight?: boolean;
  children: React.ReactNode;
}> = ({ phone, onClose, dialogRef, labelledBy, describedBy, width, role = "dialog", sheet = true, fixedHeight, children }) => {
  const pressedBackdrop = useRef(false);
  const asSheet = phone && sheet;
  return (
    <div
      className="ot-backdrop"
      // Closes only when the press started on the backdrop too, so a text selection never dismisses it.
      onMouseDown={(e) => {
        pressedBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (pressedBackdrop.current && e.target === e.currentTarget) onClose();
        pressedBackdrop.current = false;
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        display: "flex",
        alignItems: asSheet ? "flex-end" : "center",
        justifyContent: "center",
        padding: asSheet ? 0 : "16px",
        backgroundColor: "rgba(5,41,42,.40)",
        fontFamily: FONT,
      }}
    >
      <div
        ref={dialogRef}
        role={role}
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        tabIndex={-1}
        className={asSheet ? "ot-sheet" : "ot-pop"}
        style={{
          width: "100%",
          maxWidth: asSheet ? "100%" : `${width}px`,
          maxHeight: asSheet ? "88vh" : "min(720px, calc(100vh - 32px))",
          ...(fixedHeight ? { height: asSheet ? "88vh" : "min(640px, calc(100vh - 32px))" } : {}),
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#FFFFFF",
          borderRadius: asSheet ? "22px 22px 0 0" : "16px",
          boxShadow: asSheet ? "0 -12px 40px rgba(0,0,0,.14)" : "0 24px 60px rgba(5,41,42,.22)",
          outline: "none",
          overflow: "hidden",
          color: TEXT,
        }}
      >
        {asSheet && (
          <div aria-hidden="true" style={{ display: "flex", justifyContent: "center", padding: "10px 0 0", flex: "none" }}>
            <span style={{ width: "40px", height: "4px", borderRadius: "999px", backgroundColor: "#DBE4E4" }} />
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

/** Asks before unsaved edits are thrown away (Back, or the host switching company). */
export const LeaveDialog: React.FC<{
  open: boolean;
  phone: boolean;
  language: any;
  onKeep: () => void;
  onLeave: () => void;
}> = ({ open, phone, language, onKeep, onLeave }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus(open, dialogRef, onKeep);
  if (!open) return null;
  return (
    <DialogFrame
      phone={phone}
      onClose={onKeep}
      dialogRef={dialogRef}
      labelledBy="ot-leave-title"
      describedBy="ot-leave-body"
      width={420}
      role="alertdialog"
      sheet={false}
    >
      <div style={{ padding: "22px 24px 20px" }}>
        <h2 id="ot-leave-title" style={{ margin: 0, fontSize: "18px", lineHeight: "26px", fontWeight: 600, color: INK }}>
          {tr(language, "Leave without saving?")}
        </h2>
        <p id="ot-leave-body" style={{ margin: "6px 0 0", fontSize: "14px", lineHeight: "20px", color: TEXT_MUTED }}>
          {tr(language, "Your changes to this template will be lost.")}
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: "10px", marginTop: "20px" }}>
          <PillButton large={phone} onClick={onKeep}>
            {tr(language, "Keep editing")}
          </PillButton>
          <PillButton
            large={phone}
            onClick={onLeave}
            style={{ borderColor: "#E9C4C5", color: DESTRUCTIVE }}
          >
            {tr(language, "Discard and leave")}
          </PillButton>
        </div>
      </div>
    </DialogFrame>
  );
};

export const fieldStyle = (phone: boolean, invalid = false): React.CSSProperties => ({
  width: "100%",
  height: phone ? "44px" : "40px",
  padding: "0 12px",
  borderRadius: phone ? "12px" : "10px",
  border: `1px solid ${invalid ? DESTRUCTIVE : BORDER_STRONG}`,
  backgroundColor: invalid ? ERROR_BG : "#FFFFFF",
  color: TEXT,
  fontFamily: FONT,
  // 16px on phones so iOS does not zoom into the field.
  fontSize: phone ? "16px" : "14px",
  boxSizing: "border-box",
});

export const labelStyle: React.CSSProperties = {
  display: "block",
  marginBottom: "6px",
  fontSize: "13px",
  lineHeight: "18px",
  fontWeight: 500,
  color: TEXT_MUTED,
};

export const errorTextStyle: React.CSSProperties = {
  margin: "4px 0 0",
  fontSize: "12.5px",
  lineHeight: "17px",
  color: DESTRUCTIVE,
};

